import { defineStore } from 'pinia'
import { GlobalFilter } from '@/models'
import { computed, ref } from 'vue'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export const useGlobalFilterStore = defineStore('globalFilterStore', () => {
  const globalFilterService = injectTyped(ServiceKeys.globalFilter)

  // state
  const selectedGlobalFilter = ref<GlobalFilter | undefined>(undefined)
  const globalFilters = ref<GlobalFilter[]>([])

  // computed
  const selectedGlobalFilterId = computed<number | undefined>(() => selectedGlobalFilter.value?.id)
  const getGlobalFilterById = computed(() => {
    return (id: number | undefined) => globalFilters.value.find((filter) => filter.id === id)
  })

  // actions
  function addNewGlobalFilter(newGlobalFilter: GlobalFilter): void {
    globalFilters.value.push(newGlobalFilter)
  }
  function setGlobalFilters(filters: GlobalFilter[]) {
    globalFilters.value = filters
  }
  function applyGlobalFilter(globalFilter: GlobalFilter | undefined): void {
    selectedGlobalFilter.value = globalFilter
  }
  function findGlobalFilterIndex(updatedGlobalFilter: GlobalFilter): number {
    return globalFilters.value.findIndex(
      (filter: GlobalFilter) => filter.id === updatedGlobalFilter.id,
    )
  }
  function $reset() {
    setGlobalFilters([])
    applyGlobalFilter(undefined)
  }

  async function loadGlobalFiltersFromApi(
    projectId: number,
    routeFilterId?: number,
  ): Promise<void> {
    await globalFilterService
      .getGlobalFilters(projectId)
      .then(setGlobalFilters)
      .catch((error: unknown) => Promise.reject(error))

    applyGlobalFilter(getGlobalFilterById.value(routeFilterId))
  }

  async function createNewGlobalFilter(
    selectedProjectId: number,
    newGlobalFilter: Omit<GlobalFilter, 'id'>,
  ): Promise<GlobalFilter> {
    const createdGlobalFilter: GlobalFilter = await globalFilterService.createGlobalFilter(
      selectedProjectId,
      newGlobalFilter,
    )

    addNewGlobalFilter(createdGlobalFilter)
    return createdGlobalFilter
  }

  async function updateGlobalFilter(
    selectedProjectId: number,
    globalFilterToUpdate: GlobalFilter,
  ): Promise<GlobalFilter | undefined> {
    const index = findGlobalFilterIndex(globalFilterToUpdate)

    if (index !== -1) {
      const updatedFilter = await globalFilterService.updateGlobalFilter(
        selectedProjectId,
        globalFilterToUpdate,
      )
      globalFilters.value[index] = updatedFilter
      return updatedFilter
    }
  }

  async function deleteGlobalFilter(
    selectedProjectId: number,
    globalFilterToDelete: GlobalFilter,
  ): Promise<void> {
    const deletedGlobalFilter = await globalFilterService.deleteGlobalFilter(
      selectedProjectId,
      globalFilterToDelete.id,
    )

    globalFilters.value.splice(findGlobalFilterIndex(deletedGlobalFilter), 1)
  }

  return {
    // state
    selectedGlobalFilter,
    globalFilters,

    // computed
    getGlobalFilterById,
    selectedGlobalFilterId,

    // actions
    addNewGlobalFilter,
    setGlobalFilters,
    applyGlobalFilter,
    findGlobalFilterIndex,
    $reset,

    loadGlobalFiltersFromApi,
    createNewGlobalFilter,
    updateGlobalFilter,
    deleteGlobalFilter,
  }
})
